/**
 * 2026-05-19 — Carlos clarif §3 bonus: at-a-glance printer status for the POS.
 *
 * Two pills (📦 Étiquettes / 🧾 Tickets) showing whether the magasin in the
 * active POS session has each printer configured. Polls /printer/status-magasin
 * every 30s for the *latest test job result* (≤10 min window).
 *
 * Status logic (backend cannot TCP-ping a LAN IP from the VPS):
 *   - "🟢 OK"       → printer IP set + last test succeeded recently
 *   - "🟡 Configurée" → printer IP set, but no recent test result
 *   - "🔴 Non configurée" → printer IP missing
 */
import React, { useEffect, useState } from 'react';
import { Tag, Receipt } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const POLL_INTERVAL_MS = 30_000;

const STATUS_STYLES = {
  ok:     { dot: 'bg-emerald-400', text: 'text-emerald-300', border: 'border-emerald-500/30 bg-emerald-500/10', label: 'OK' },
  warn:   { dot: 'bg-amber-400',   text: 'text-amber-300',   border: 'border-amber-500/30 bg-amber-500/10',   label: 'Configurée' },
  off:    { dot: 'bg-gray-500',    text: 'text-gray-400',    border: 'border-gray-500/30 bg-gray-500/10',     label: 'Non configurée' },
  fail:   { dot: 'bg-red-400',     text: 'text-red-300',     border: 'border-red-500/30 bg-red-500/10',       label: 'Échec dernier test' },
};

const Pill = ({ icon: Icon, name, status, ip, port, testId }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.off;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${style.border} text-[11px] font-mono`}
      data-testid={testId}
      title={ip ? `${ip}:${port || 9100}` : 'IP non configurée'}
    >
      <Icon className={`w-3.5 h-3.5 ${style.text}`} />
      <span className={`${style.text} font-semibold`}>{name}</span>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
      <span className={style.text}>{style.label}</span>
    </div>
  );
};

const _statusOf = (printer) => {
  if (!printer || !printer.configured) return 'off';
  if (printer.last_test === 'done' || printer.last_test === 'completed') return 'ok';
  if (printer.last_test === 'failed' || printer.last_test === 'error') return 'fail';
  return 'warn';
};

const PrinterStatusBadges = ({ magasinCode }) => {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!magasinCode) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await api.get(`/printer/status-magasin/${magasinCode}`);
        if (!cancelled) setData(res.data || null);
      } catch (_) {
        if (!cancelled) setData(null);
      }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [magasinCode, api]);

  // Hide for non-admins (Carlos-only info per spec).
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) return null;
  if (!magasinCode) return null;

  const labelStatus = _statusOf(data?.label_printer);
  const ticketStatus = _statusOf(data?.ticket_printer);

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 mb-3"
      data-testid="pos-printer-status-badges"
    >
      <span className="text-[10px] uppercase text-gray-500 tracking-wider mr-1">
        Imprimantes {magasinCode}
      </span>
      <Pill
        icon={Tag}
        name="Étiquettes"
        status={labelStatus}
        ip={data?.label_printer?.ip}
        port={data?.label_printer?.port}
        testId="pos-printer-status-label"
      />
      <Pill
        icon={Receipt}
        name="Tickets"
        status={ticketStatus}
        ip={data?.ticket_printer?.ip}
        port={data?.ticket_printer?.port}
        testId="pos-printer-status-ticket"
      />
    </div>
  );
};

export default PrinterStatusBadges;
